import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";

import { useSession } from "../auth/session";
import { Button } from "../components/Button";
import { ClientSetupShell } from "../components/ClientSetupShell";
import { CountryCombobox } from "../components/CountryCombobox";
import { Notice, Skeleton } from "../components/Feedback";
import { RequestNotice } from "../components/RequestNotice";
import { Field } from "../components/FormControls";
import { navigate } from "../routing/useLocationPath";
import { clientRequest, type BillingAddress, type ClientRecord, type ContactRecord } from "./clientApi";
import { clientRecordPath } from "./clientCollection";
import { isValidEmailAddress } from "../../shared/email";
import { suggestedClientInvoicePrefix } from "../../shared/invoice-prefix";

interface ContactDraft {
  id?: string;
  version?: number;
  name: string;
  email: string;
  phone: string;
}

interface SetupDraft {
  displayName: string;
  invoicePrefix: string;
  primaryContact: ContactDraft;
  additionalContacts: ContactDraft[];
  billingAddress: Record<keyof BillingAddress, string>;
}

const blankContact = (): ContactDraft => ({ name: "", email: "", phone: "" });
const blankAddress = (): SetupDraft["billingAddress"] => ({ line1: "", line2: "", locality: "", region: "", postalCode: "", country: "" });
const blankDraft = (): SetupDraft => ({ displayName: "", invoicePrefix: "", primaryContact: blankContact(), additionalContacts: [], billingAddress: blankAddress() });

function normalizeContact(contact: ContactDraft) {
  return { name: contact.name.trim(), email: contact.email.trim() || null, phone: contact.phone.trim() || null };
}

function hasContactValue(contact: ContactDraft) {
  return Boolean(contact.name.trim() || contact.email.trim() || contact.phone.trim());
}

function validEmail(value: string) {
  return !value.trim() || isValidEmailAddress(value.trim());
}

export function ClientSetupPage({ clientId, mode }: { clientId?: string; mode: "create" | "edit" }) {
  const { getToken, sessionId, state: sessionState } = useSession();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const formId = useId();
  const [draft, setDraft] = useState<SetupDraft>(blankDraft);
  const [clientVersion, setClientVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(mode === "edit");
  const [loadError, setLoadError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [contactsExpanded, setContactsExpanded] = useState(false);
  const [baseline, setBaseline] = useState(() => JSON.stringify(blankDraft()));
  const bypassExitRef = useRef(false);
  const prefixEdited = useRef(false);

  const destination = mode === "edit" && clientId ? clientRecordPath(clientId) : "/clients";
  const dirty = JSON.stringify(draft) !== baseline;
  const requestExit = useCallback(() => {
    if (dirty && !window.confirm("Discard unsaved client changes?")) return;
    bypassExitRef.current = true;
    navigate(destination);
  }, [destination, dirty]);

  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    const popState = () => {
      if (bypassExitRef.current || window.confirm("Discard unsaved client changes?")) return;
      window.history.forward();
    };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("popstate", popState);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("popstate", popState);
    };
  }, [dirty]);

  useEffect(() => {
    if (mode !== "edit" || !clientId || sessionState !== "signed-in" || !sessionId) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    void Promise.all([
      clientRequest<ClientRecord>(getTokenRef.current, `/api/clients/${clientId}`, { signal: controller.signal }),
      clientRequest<{ contacts: ContactRecord[] }>(getTokenRef.current, `/api/clients/${clientId}/contacts?state=active`, { signal: controller.signal }),
    ]).then(([client, contacts]) => {
      if (controller.signal.aborted) return;
      const primary = contacts.contacts.find((contact) => contact.isPrimary);
      const loadedDraft: SetupDraft = {
        displayName: client.displayName,
        invoicePrefix: client.invoicePrefix ?? "",
        billingAddress: Object.fromEntries(Object.entries({ ...blankAddress(), ...client.billingAddress }).map(([key, value]) => [key, value ?? ""])) as SetupDraft["billingAddress"],
        primaryContact: primary ? { id: primary.id, version: primary.version, name: primary.name, email: primary.email ?? "", phone: primary.phone ?? "" } : blankContact(),
        additionalContacts: contacts.contacts.filter((contact) => !contact.isPrimary).map((contact) => ({ id: contact.id, version: contact.version, name: contact.name, email: contact.email ?? "", phone: contact.phone ?? "" })),
      };
      setDraft(loadedDraft);
      setBaseline(JSON.stringify(loadedDraft));
      setAddressExpanded(Object.values(loadedDraft.billingAddress).some((value) => value.trim()));
      setContactsExpanded(loadedDraft.additionalContacts.length > 0);
      setClientVersion(client.version);
      setLoading(false);
    }).catch(() => {
      if (!controller.signal.aborted) { setLoading(false); setLoadError("This client setup could not be loaded."); }
    });
    return () => controller.abort();
  }, [clientId, mode, retryKey, sessionId, sessionState]);

  function validate() {
    const errors: Record<string, string> = {};
    if (!draft.displayName.trim()) errors.displayName = "Enter the client or business name.";
    if (draft.invoicePrefix.trim() && !/^[A-Z0-9]{2,8}$/.test(draft.invoicePrefix.trim().toUpperCase())) errors.invoicePrefix = "Use 2–8 letters or digits.";
    if (hasContactValue(draft.primaryContact) && !draft.primaryContact.name.trim() && !draft.primaryContact.email.trim()) errors.primaryEmail = "Enter a contact name or email address.";
    if (!validEmail(draft.primaryContact.email)) errors.primaryEmail = "Enter a valid email address.";
    draft.additionalContacts.forEach((contact, index) => {
      if (!contact.id && !hasContactValue(contact)) return;
      if (!contact.name.trim() && !contact.email.trim()) errors[`contact-${index}-email`] = "Enter a contact name or email address.";
      if (!validEmail(contact.email)) errors[`contact-${index}-email`] = "Enter a valid email address.";
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!validate() || saving) return;
    setSaving(true);
    setRequestError("");
    const billingAddress: BillingAddress = {
      line1: draft.billingAddress.line1.trim() || null,
      line2: draft.billingAddress.line2.trim() || null,
      locality: draft.billingAddress.locality.trim() || null,
      region: draft.billingAddress.region.trim() || null,
      postalCode: draft.billingAddress.postalCode.trim() || null,
      country: draft.billingAddress.country.trim() || null,
    };
    try {
      if (mode === "create") {
        const created = await clientRequest<ClientRecord>(getTokenRef.current, "/api/clients", {
          method: "POST",
          body: JSON.stringify({
            displayName: draft.displayName.trim(),
            invoicePrefix: draft.invoicePrefix.trim().toUpperCase() || null,
            billingAddress,
            primaryContact: hasContactValue(draft.primaryContact) ? normalizeContact(draft.primaryContact) : null,
            additionalContacts: draft.additionalContacts.filter(hasContactValue).map(normalizeContact),
          }),
        });
        bypassExitRef.current = true;
        navigate(clientRecordPath(created.id));
        return;
      }
      if (!clientId || !clientVersion) throw new Error("This client setup is unavailable.");
      await clientRequest<ClientRecord>(getTokenRef.current, `/api/clients/${clientId}`, {
        method: "PATCH",
        body: JSON.stringify({ displayName: draft.displayName.trim(), invoicePrefix: draft.invoicePrefix.trim().toUpperCase() || null, billingAddress, version: clientVersion }),
      });
      if (hasContactValue(draft.primaryContact)) {
        await saveContact(clientId, draft.primaryContact, true);
      }
      for (const contact of draft.additionalContacts.filter((contact) => Boolean(contact.id) || hasContactValue(contact))) await saveContact(clientId, contact, false);
      bypassExitRef.current = true;
      navigate(clientRecordPath(clientId));
    } catch (failure) {
      setRequestError(failure instanceof Error ? failure.message : "The client could not be saved.");
      setSaving(false);
    }
  }

  async function saveContact(ownerClientId: string, contact: ContactDraft, isPrimary: boolean) {
    await clientRequest(getTokenRef.current, contact.id ? `/api/clients/${ownerClientId}/contacts/${contact.id}` : `/api/clients/${ownerClientId}/contacts`, {
      method: contact.id ? "PATCH" : "POST",
      body: JSON.stringify({ ...normalizeContact(contact), isPrimary, ...(contact.id ? { version: contact.version } : {}) }),
    });
  }

  function addressField(key: keyof BillingAddress, label: string, maximum: number) {
    return <Field label={label} maxLength={maximum} value={draft.billingAddress[key] ?? ""} onChange={(event) => setDraft((current) => ({ ...current, billingAddress: { ...current.billingAddress, [key]: event.target.value } }))} />;
  }

  return (
    <ClientSetupShell canSave={Boolean(draft.displayName.trim())} formId={formId} saving={saving} title={mode === "create" ? "New client" : "Edit client"} onBack={requestExit}>
      {loading ? <div className="record-setup-loading" aria-label="Loading client setup"><Skeleton height={120} /><Skeleton height={240} /><Skeleton height={180} /></div> : loadError ? <Notice title="Client setup unavailable" tone="danger">{loadError} <Button variant="secondary" onClick={() => setRetryKey((value) => value + 1)}>Retry</Button></Notice> : (
        <form id={formId} className="record-setup-form" onSubmit={submit} noValidate>
          {requestError ? <RequestNotice title="Client not saved" message={requestError} /> : null}
          <section className="setup-section" aria-labelledby="client-identity-heading">
            <div className="setup-section-heading"><p className="eyebrow">Client</p><h2 id="client-identity-heading">Who are you billing?</h2></div>
            <Field autoFocus label="Client or business name" maxLength={200} required value={draft.displayName} error={fieldErrors.displayName} onChange={(event) => { const displayName = event.target.value; setDraft((current) => ({ ...current, displayName, ...(mode === "create" && !prefixEdited.current ? { invoicePrefix: displayName.trim() ? suggestedClientInvoicePrefix(displayName, "") : "" } : {}) })); setFieldErrors((current) => ({ ...current, displayName: "" })); }} />
            <Field label="Invoice prefix (optional)" maxLength={8} value={draft.invoicePrefix} error={fieldErrors.invoicePrefix} hint="Prefix only, 2–8 letters or digits. The invoice sequence is added separately. Leave empty for automatic numbering." onChange={(event) => { prefixEdited.current = true; setDraft(current => ({ ...current, invoicePrefix: event.target.value.toUpperCase() })); setFieldErrors(current => ({ ...current, invoicePrefix: "" })); }} />
          </section>

          <section className="setup-section" aria-labelledby="primary-contact-heading">
            <div className="setup-section-heading"><p className="eyebrow">Primary contact</p><h2 id="primary-contact-heading">Who should receive billing?</h2><p>Optional now. An email is needed before a document can be sent.</p></div>
            <div className="setup-field-grid setup-field-grid--contact">
              <Field label="Primary contact name" maxLength={200} value={draft.primaryContact.name} error={fieldErrors.primaryName} onChange={(event) => setDraft((current) => ({ ...current, primaryContact: { ...current.primaryContact, name: event.target.value } }))} />
              <Field label="Email" type="email" maxLength={320} value={draft.primaryContact.email} error={fieldErrors.primaryEmail} onChange={(event) => setDraft((current) => ({ ...current, primaryContact: { ...current.primaryContact, email: event.target.value } }))} />
              <Field label="Phone" type="tel" maxLength={50} value={draft.primaryContact.phone} onChange={(event) => setDraft((current) => ({ ...current, primaryContact: { ...current.primaryContact, phone: event.target.value } }))} />
            </div>
          </section>

          <section className="setup-section" aria-labelledby="billing-address-heading">
            <div className="setup-section-heading"><p className="eyebrow">Billing address</p><h2 id="billing-address-heading">Billing address</h2>{addressExpanded ? <p>Optional. Complete what is useful now and return later.</p> : null}</div>
            {!addressExpanded ? <Button type="button" variant="quiet" className="setup-inline-action" onClick={() => setAddressExpanded(true)}>+ Add billing address</Button> : <div className="setup-field-grid">
              <div className="setup-field-span">{addressField("line1", "Billing address line 1", 200)}</div>
              <div className="setup-field-span">{addressField("line2", "Billing address line 2", 200)}</div>
              {addressField("locality", "City or locality", 120)}
              {addressField("region", draft.billingAddress.country === "US" ? "State" : draft.billingAddress.country === "CA" ? "Province" : "State or region", 120)}
              {addressField("postalCode", draft.billingAddress.country === "US" ? "ZIP code" : "Postal code", 40)}
              <CountryCombobox value={draft.billingAddress.country} onChange={(country) => setDraft((current) => ({ ...current, billingAddress: { ...current.billingAddress, country } }))} />
              <Button type="button" variant="quiet" className="setup-inline-action" onClick={() => setAddressExpanded(false)}>Hide billing address fields</Button>
            </div>}
          </section>

          <section className="setup-section" aria-labelledby="additional-contacts-heading">
            <div className="setup-section-heading"><p className="eyebrow">Additional contacts</p><h2 id="additional-contacts-heading">Other people</h2><p>Add only the contacts needed for this client.</p></div>
            {contactsExpanded ? <div className="additional-contact-list">
              {draft.additionalContacts.map((contact, index) => (
                <fieldset key={contact.id ?? index} className="additional-contact">
                  <legend>Contact {index + 2}</legend>
                  <div className="setup-field-grid setup-field-grid--contact">
                    <Field label="Contact name" maxLength={200} value={contact.name} error={fieldErrors[`contact-${index}-name`]} onChange={(event) => setDraft((current) => ({ ...current, additionalContacts: current.additionalContacts.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item) }))} />
                    <Field label="Email" type="email" maxLength={320} value={contact.email} error={fieldErrors[`contact-${index}-email`]} onChange={(event) => setDraft((current) => ({ ...current, additionalContacts: current.additionalContacts.map((item, itemIndex) => itemIndex === index ? { ...item, email: event.target.value } : item) }))} />
                    <Field label="Phone" type="tel" maxLength={50} value={contact.phone} onChange={(event) => setDraft((current) => ({ ...current, additionalContacts: current.additionalContacts.map((item, itemIndex) => itemIndex === index ? { ...item, phone: event.target.value } : item) }))} />
                  </div>
                  {!contact.id ? <Button type="button" variant="quiet" onClick={() => setDraft((current) => ({ ...current, additionalContacts: current.additionalContacts.filter((_, itemIndex) => itemIndex !== index) }))}>Remove contact</Button> : null}
                </fieldset>
              ))}
            </div> : null}
            {!contactsExpanded ? <Button type="button" variant="quiet" className="setup-inline-action" onClick={() => { setContactsExpanded(true); setDraft((current) => current.additionalContacts.length ? current : { ...current, additionalContacts: [blankContact()] }); }}>+ Add additional contact</Button> : draft.additionalContacts.length < 10 ? <Button type="button" variant="quiet" className="setup-inline-action" onClick={() => setDraft((current) => ({ ...current, additionalContacts: [...current.additionalContacts, blankContact()] }))}>+ Add another contact</Button> : null}
          </section>
        </form>
      )}
    </ClientSetupShell>
  );
}
